import React, { useState, useEffect, useRef } from 'react';
import Pagination from './Pagination';
import '../../styles/admin-table-vis.css';

const DataTable = ({
  tableId,
  columns,
  data,
  loading,
  onRowClick,
  emptyState = {
    title: 'Veri Bulunamadı',
    message: 'Gösterilecek kayıt bulunmuyor.',
    icon: 'folder-open'
  },
  pagination,
  onPageChange,
  onLimitChange
}) => {
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const dropdownRef = useRef(null);

  // Initialize visible columns from localStorage or props
  useEffect(() => {
    if (tableId) {
      const saved = localStorage.getItem(`kito_table_columns_${tableId}`);
      if (saved) {
        try {
          const savedCols = JSON.parse(saved);
          // Only use saved columns that actually exist in the current columns prop
          const validCols = savedCols.filter(sc => columns.some(c => c.accessor === sc || (c.header === sc && !c.accessor)));
          if (validCols.length > 0) {
            setVisibleColumns(validCols);
            return;
          }
        } catch (e) {
          console.error('Error parsing saved columns:', e);
        }
      }
    }
    // Default: all columns visible
    setVisibleColumns(columns.map(c => c.accessor || c.header));
  }, [tableId, columns.length]);

  // Persist settings
  useEffect(() => {
    if (tableId && visibleColumns.length > 0) {
      localStorage.setItem(`kito_table_columns_${tableId}`, JSON.stringify(visibleColumns));
    }
  }, [visibleColumns, tableId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowColumnToggle(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleColumn = (colId) => {
    setVisibleColumns(prev => {
      if (prev.includes(colId)) {
        // Don't hide the last column
        if (prev.length <= 1) return prev;
        return prev.filter(c => c !== colId);
      } else {
        return [...prev, colId];
      }
    });
  };

  const activeColumns = columns.filter(c => visibleColumns.includes(c.accessor || c.header));

  const hasStaleRows = loading && Array.isArray(data) && data.length > 0;

  // Skeleton Loader Component
  const TableSkeleton = () => (
    <>
      {[...Array(5)].map((_, idx) => (
        <tr key={`skel-${idx}`}>
          {activeColumns.map((col, cIdx) => (
            <td key={cIdx}>
              <div className="placeholder-glow">
                <span
                  className="placeholder col-10 rounded"
                  style={{ minHeight: '0.9rem', backgroundColor: '#cbd5e1' }}
                />
              </div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="admin-card bg-white rounded shadow-sm border-0 position-relative">
      {/* Column Visibility Toolbar */}
      <div className="d-flex justify-content-between align-items-center p-2 border-bottom bg-light rounded-top" ref={dropdownRef}>
        <div className="text-muted small fw-medium ms-2 d-flex align-items-center gap-2">
          <i className="fas fa-table me-1"></i>
          <span>{pagination ? `${pagination.total} Kayıt` : 'Veri Listesi'}</span>
          {loading && (
            <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
          )}
        </div>
        <div className="position-relative">
          <button
            className="btn btn-sm btn-white border shadow-sm text-dark d-flex align-items-center fw-medium"
            onClick={() => setShowColumnToggle(!showColumnToggle)}
            title="Sütunları Yönet"
          >
            <i className="fas fa-columns me-2 text-primary"></i>
            Sütunları Düzenle
          </button>

          {showColumnToggle && (
            <div className="position-absolute end-0 mt-2 bg-white border rounded shadow-lg p-2" style={{ minWidth: '220px', zIndex: 1050 }}>
              <div className="d-flex justify-content-between align-items-center px-2 py-1 mb-2 border-bottom">
                <h6 className="dropdown-header p-0 small text-uppercase fw-bold text-dark mb-0">Tablo Sütunları</h6>
                <button
                  className="btn btn-xs btn-link p-0 text-decoration-none small text-danger"
                  onClick={() => setShowColumnToggle(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="max-h-200 overflow-auto custom-scrollbar" style={{ maxHeight: '300px' }}>
                {columns.map((col, idx) => {
                  const colId = col.accessor || col.header;
                  const isVisible = visibleColumns.includes(colId);
                  return (
                    <div key={idx} className="small px-3 py-2 mb-0 hover-bg-light rounded cursor-pointer d-flex align-items-center" onClick={() => toggleColumn(colId)}>
                      <div className="me-2 d-flex align-items-center justify-content-center" style={{ width: '16px' }}>
                        <i className={`fa${isVisible ? 's' : 'r'} fa-${isVisible ? 'check-square text-primary' : 'square text-muted'} fs-6`}></i>
                      </div>
                      <span className="text-nowrap flex-grow-1" style={{ cursor: 'pointer', userSelect: 'none' }}>
                        {col.header}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="border-top mt-2 pt-2 px-1 d-flex gap-2">
                <button
                  className="btn btn-xs btn-light w-50 text-center border"
                  onClick={() => setVisibleColumns(columns.map(c => c.accessor || c.header))}
                >
                  Tümünü Seç
                </button>
                <button
                  className="btn btn-xs btn-light w-50 text-center border"
                  onClick={() => setVisibleColumns([columns[0].accessor || columns[0].header])} // Keep at least one
                >
                  Temizle
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="table-responsive table-responsive-desktop-visible position-relative">
        {hasStaleRows && (
          <>
            <div
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{
                backgroundColor: 'rgba(255,255,255,0.72)',
                zIndex: 2,
                pointerEvents: 'none'
              }}
            />
            <div
              className="position-absolute start-50 translate-middle-x d-flex align-items-center gap-2 px-3 py-2 rounded shadow-sm border bg-white small text-dark fw-medium"
              style={{ top: '12px', zIndex: 3, pointerEvents: 'none' }}
            >
              <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
              Güncelleniyor...
            </div>
          </>
        )}
        <table className="table table-hover align-middle mb-0 admin-table" style={{ width: '100%', tableLayout: 'auto' }}>
          <thead className="bg-light">
            <tr>
              {activeColumns.map((col, idx) => (
                <th
                  key={idx}
                  scope="col"
                  className="py-3 px-4 border-0"
                  style={{
                    minWidth: col.width || undefined,
                    whiteSpace: 'nowrap',
                    ...col.style
                  }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="border-top-0">
            {loading && !hasStaleRows ? (
              <TableSkeleton />
            ) : data && data.length > 0 ? (
              data.map((row, rIdx) => (
                <tr
                  key={row.id || rIdx}
                  onClick={() => !loading && onRowClick && onRowClick(row)}
                  className={onRowClick && !loading ? 'cursor-pointer' : ''}
                  style={{
                    transition: 'all 0.2s',
                    opacity: hasStaleRows ? 0.88 : 1,
                    pointerEvents: hasStaleRows ? 'none' : undefined
                  }}
                >
                  {activeColumns.map((col, cIdx) => (
                    <td key={cIdx} className="px-4 py-3">
                      {col.render ? col.render(row) : (row[col.accessor] || '-')}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={activeColumns.length} className="text-center py-5">
                  <div className="d-flex flex-column align-items-center justify-content-center text-muted">
                    <div className="bg-light rounded-circle p-3 mb-3">
                      <i className={`fas fa-${emptyState.icon} fa-2x`}></i>
                    </div>
                    <h6 className="fw-bold mb-1">{emptyState.title}</h6>
                    <p className="small mb-0">{emptyState.message}</p>
                    {emptyState.action && (
                      <div className="mt-3">
                        {emptyState.action}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {data && data.length > 0 && pagination && (
        <div style={loading ? { opacity: 0.65, pointerEvents: 'none' } : undefined}>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            limit={pagination.limit}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      )}

      <style>{`
        .hover-bg-light:hover { background-color: #f8f9fa; }
        .btn-xs { font-size: 0.75rem; padding: 0.1rem 0.4rem; }
        @media (max-width: 767px) {
          .admin-table th, .admin-table td {
            min-width: unset !important;
            padding-left: 0.75rem !important;
            padding-right: 0.75rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DataTable;
